import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PhotoPickerDrawer } from './components/PhotoPickerDrawer';
import { PhotoPickerDropzone } from './components/PhotoPickerDropzone';
import { PhotoPickerGrid } from './components/PhotoPickerGrid';
import { PhotoPickerTrigger } from './components/PhotoPickerTrigger';
import { DEFAULT_PHOTO_PICKER_COPY } from './copy';
import { useBlobPreviews } from './hooks/useBlobPreviews';
import { usePhotoPickerUploads } from './hooks/usePhotoPickerUploads';
import { buildRequirementsLines } from './requirements';
import type { PhotoPickerProps } from './types';
import * as S from './PhotoPicker.styles';

export function PhotoPicker({
  acceptedFileTypes,
  copy,
  describedById,
  max = 10,
  maxFileSizeBytes,
  min = 0,
  onChange,
  onClose: onCloseProp,
  onConfirm: onConfirmProp,
  open: openProp,
  previewFor: previewForProp,
  requestUploadSlots,
  required,
  triggerLabel,
  uploadFiles,
  value,
}: PhotoPickerProps) {
  const isControlled = openProp !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = isControlled ? openProp : internalOpen;

  const mergedCopy = useMemo(() => ({ ...DEFAULT_PHOTO_PICKER_COPY, ...copy }), [copy]);

  const requirements = useMemo(
    () => buildRequirementsLines(acceptedFileTypes, maxFileSizeBytes),
    [acceptedFileTypes, maxFileSizeBytes],
  );

  const blob = useBlobPreviews();
  const uploads = usePhotoPickerUploads({ isOpen, max, requestUploadSlots, uploadFiles });

  // Draft URLs shown inside the drawer while it's open. `null` when closed.
  // Selected files sit in `pendingFiles` as blob URLs until confirm; the PUT
  // upload only runs on confirm. Deletes/reorders mutate the draft locally;
  // `onChange` only fires on confirm. Works the same for fresh PDP and
  // basket-edit flows.
  const [draftUrls, setDraftUrls] = useState<string[] | null>(null);
  const pendingFilesRef = useRef<Map<string, File>>(new Map());
  const drawerUrls = draftUrls ?? value;

  useEffect(() => {
    if (isOpen) {
      setDraftUrls(value);
      return;
    }
    setDraftUrls(null);
    pendingFilesRef.current.forEach((_file, blobUrl) => URL.revokeObjectURL(blobUrl));
    pendingFilesRef.current = new Map();
    // Re-seed only when the drawer transitions open/closed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Revoke any still-pending blob URLs on unmount.
  useEffect(
    () => () => {
      pendingFilesRef.current.forEach((_file, blobUrl) => URL.revokeObjectURL(blobUrl));
    },
    [],
  );

  const previewFor = useCallback(
    (url: string) => blob.getPreview(url) ?? previewForProp?.(url),
    [blob, previewForProp],
  );

  const handleOpen = useCallback(() => {
    uploads.resetErrors();
    setInternalOpen(true);
  }, [uploads]);

  const closeDrawer = useCallback(() => {
    if (isControlled) {
      onCloseProp?.();
    } else {
      setInternalOpen(false);
    }
  }, [isControlled, onCloseProp]);

  // Pending blob URLs are revoked by the isOpen effect when the drawer closes.
  const handleClose = closeDrawer;

  const handleConfirm = useCallback(async () => {
    const current = draftUrls ?? value;
    const pending = pendingFilesRef.current;
    let next = current;

    if (pending.size > 0) {
      // Collect pending blob URLs in draft order so the final list keeps the
      // user's chosen order (including any drag-reorders made in the drawer).
      const blobUrlsInOrder = current.filter((u) => pending.has(u));
      const filesInOrder = blobUrlsInOrder.map((b) => pending.get(b) as File);
      const uploaded = await uploads.upload(filesInOrder);
      if (uploaded.length !== filesInOrder.length) {
        // Upload failed — keep drawer open; error UI is surfaced by uploads.uploadError.
        return;
      }
      const blobToObject = new Map<string, string>();
      blobUrlsInOrder.forEach((b, i) => {
        blobToObject.set(b, uploaded[i]);
        // Attach a fresh blob preview keyed by the new object URL so the
        // trigger thumbnail keeps rendering immediately after confirm.
        blob.attach(uploaded[i], pending.get(b) as File);
      });
      next = current.map((u) => blobToObject.get(u) ?? u);
    }

    // Detach blob previews for previously-committed URLs the user removed.
    value.forEach((u) => {
      if (!next.includes(u)) {
        blob.detach(u);
      }
    });

    onChange(next);
    if (onConfirmProp) {
      onConfirmProp();
    } else {
      closeDrawer();
    }
  }, [draftUrls, value, uploads, blob, onChange, onConfirmProp, closeDrawer]);

  const handleFiles = useCallback(
    (files: File[]) => {
      const current = draftUrls ?? value;
      const room = Math.max(0, max - current.length);
      const trimmed = files.slice(0, room);
      if (trimmed.length === 0) {
        return;
      }
      // Stage the file locally. The PUT runs on confirm; until then the blob
      // URL serves as both the draft entry and the preview src.
      const blobUrls = trimmed.map((f) => {
        const blobUrl = URL.createObjectURL(f);
        pendingFilesRef.current.set(blobUrl, f);
        return blobUrl;
      });
      setDraftUrls([...current, ...blobUrls]);
    },
    [draftUrls, value, max],
  );

  const handleDelete = useCallback(
    (url: string) => {
      if (pendingFilesRef.current.has(url)) {
        URL.revokeObjectURL(url);
        pendingFilesRef.current.delete(url);
      }
      setDraftUrls((current) => (current ?? value).filter((u) => u !== url));
    },
    [value],
  );

  const handleReorder = useCallback(
    (from: number, to: number) => {
      setDraftUrls((current) => {
        const next = (current ?? value).slice();
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        return next;
      });
    },
    [value],
  );

  return (
    <>
      {!isControlled && (
        <PhotoPickerTrigger
          addMoreLabel={mergedCopy.triggerAddMoreLabel}
          emptyLabel={mergedCopy.triggerEmptyLabel}
          onClick={handleOpen}
          previewFor={previewFor}
          triggerLabel={triggerLabel}
          urls={value}
        />
      )}
      <PhotoPickerDrawer
        cancelLabel={mergedCopy.cancelLabel}
        confirmDisabled={drawerUrls.length === 0 || uploads.isUploading}
        confirmLabel={mergedCopy.confirmLabel}
        describedById={describedById}
        heading={mergedCopy.drawerHeading}
        isOpen={isOpen}
        max={max}
        onCancel={handleClose}
        onConfirm={handleConfirm}
        requirements={requirements}
        subDescription={mergedCopy.drawerSubDescription}
      >
        {drawerUrls.length > 0 && (
          <PhotoPickerGrid
            deleteLabel={mergedCopy.deleteLabel}
            onDelete={handleDelete}
            onReorder={handleReorder}
            previewFor={previewFor}
            urls={drawerUrls}
          />
        )}
        {drawerUrls.length < max && (
          <PhotoPickerDropzone
            acceptedFileTypes={acceptedFileTypes}
            addMoreLabel={mergedCopy.dropzoneAddMoreLabel}
            emptyBody={mergedCopy.dropzoneEmptyBody}
            emptyTitle={mergedCopy.dropzoneEmptyTitle}
            isEmpty={drawerUrls.length === 0}
            maxFileSizeBytes={maxFileSizeBytes}
            onFilesSelected={handleFiles}
          />
        )}
        {uploads.isUploading && <S.StatusMessage>{mergedCopy.uploadingLabel}</S.StatusMessage>}
        {uploads.uploadError && (
          <S.ErrorMessage role="alert">{mergedCopy.uploadErrorMessage}</S.ErrorMessage>
        )}
        {uploads.presignError && (
          <S.ErrorMessage role="alert">{mergedCopy.presignErrorMessage}</S.ErrorMessage>
        )}
        {required && min > 0 && drawerUrls.length < min && (
          <S.MinWarning>
            Please add at least {min} photo{min === 1 ? '' : 's'}.
          </S.MinWarning>
        )}
      </PhotoPickerDrawer>
    </>
  );
}
